import { apiClient } from './client'

// ==================== TYPES ====================

export type AudienceType =
  | 'all_school'
  | 'grades'
  | 'streams'
  | 'grade_parents'
  | 'stream_parents'
  | 'individual_users'
  | 'parents_only'
  | 'staff_only'

export type DeliveryChannel = 'inApp' | 'email' | 'sms'

export type AnnouncementStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed' | 'cancelled'

export type AnnouncementPriority = 'low' | 'normal' | 'high' | 'urgent'

export type EventType = 'academic' | 'sports' | 'cultural' | 'holiday' | 'exam' | 'meeting' | 'parent_teacher' | 'other'

export type NotificationType =
  | 'announcement'
  | 'event_reminder'
  | 'attendance_alert'
  | 'transport_update'
  | 'academic_update'
  | 'fee_reminder'
  | 'library_notice'
  | 'system'

// ==================== INTERFACES ====================

export interface TargetAudience {
  type: AudienceType
  roles?: string[]
  gradeIds?: string[]
  streamIds?: string[]
  userIds?: string[]
}

export interface DeliveryChannels {
  inApp: boolean
  email: boolean
  sms: boolean
}

export interface DeliveryStats {
  totalRecipients: number
  inAppDelivered: number
  inAppRead: number
  emailDelivered: number
  emailFailed: number
  smsDelivered: number
  smsFailed: number
  acknowledged: number
}

export interface Attachment {
  name: string
  url: string
  type: string
  size: number
}

export interface ExternalLink {
  label: string
  url: string
}

// ==================== ANNOUNCEMENT ====================

export interface Announcement {
  id: string
  _id: string
  institutionId: string
  title: string
  content: string
  htmlContent?: string
  targetAudience: TargetAudience
  channels: DeliveryChannels
  status: AnnouncementStatus
  priority: AnnouncementPriority
  scheduledAt?: string
  sentAt?: string
  deliveryStats?: DeliveryStats
  attachments?: Attachment[]
  acknowledgmentRequired: boolean
  acknowledgmentDeadline?: string
  externalLinks?: ExternalLink[]
  templateId?: string
  isActive: boolean
  createdById: string
  createdBy?: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
  createdAt: string
  updatedAt: string
}

export interface CreateAnnouncementRequest {
  title: string
  content: string
  htmlContent?: string
  targetAudience: TargetAudience
  channels: DeliveryChannels
  priority?: AnnouncementPriority
  scheduledAt?: string
  acknowledgmentRequired?: boolean
  acknowledgmentDeadline?: string
  externalLinks?: ExternalLink[]
  institutionId?: string
}

export interface UpdateAnnouncementRequest {
  title?: string
  content?: string
  htmlContent?: string
  targetAudience?: TargetAudience
  channels?: DeliveryChannels
  priority?: AnnouncementPriority
  scheduledAt?: string | null
  acknowledgmentRequired?: boolean
  acknowledgmentDeadline?: string
}

export interface GetAnnouncementsParams {
  page?: number
  limit?: number
  search?: string
  status?: AnnouncementStatus
  priority?: AnnouncementPriority
  audienceType?: AudienceType
  startDate?: string
  endDate?: string
  sortBy?: 'createdAt' | 'scheduledAt' | 'sentAt' | 'title'
  sortOrder?: 'asc' | 'desc'
  institutionId?: string
}

export interface AnnouncementStats {
  totalAnnouncements: number
  sent: number
  scheduled: number
  drafts: number
  sentToday: number
  totalReach: number
  averageReadRate: number
}

// ==================== EVENT ====================

export interface EventLocation {
  name: string
  address?: string
  latitude?: number
  longitude?: number
  isVirtual: boolean
  meetingUrl?: string
}

export interface EventReminder {
  type: DeliveryChannel
  minutesBefore: number
  sent: boolean
  sentAt?: string
}

export interface EventRsvp {
  userId: string
  status: 'pending' | 'attending' | 'maybe' | 'declined'
  respondedAt: string
  notes?: string
  user?: {
    firstName: string
    lastName: string
    email: string
  }
}

export interface CalendarEvent {
  id: string
  _id: string
  institutionId: string
  title: string
  description?: string
  startDate: string
  endDate: string
  isAllDay: boolean
  timezone: string
  eventType: EventType
  color?: string
  location?: EventLocation
  visibility: 'public' | 'private' | 'targeted'
  targetAudience?: TargetAudience
  reminders?: EventReminder[]
  notifyOnCreate: boolean
  notifyChannels?: DeliveryChannel[]
  requiresRsvp: boolean
  rsvpDeadline?: string
  rsvps?: EventRsvp[]
  maxAttendees?: number
  attachments?: Attachment[]
  status: 'draft' | 'published' | 'cancelled'
  isActive: boolean
  createdById: string
  createdBy?: {
    id: string
    firstName: string
    lastName: string
  }
  createdAt: string
  updatedAt: string
}

export interface CreateEventRequest {
  title: string
  description?: string
  startDate: string
  endDate: string
  isAllDay?: boolean
  timezone?: string
  eventType: EventType
  color?: string
  location?: EventLocation
  visibility?: 'public' | 'private' | 'targeted'
  targetAudience?: TargetAudience
  reminders?: Omit<EventReminder, 'sent' | 'sentAt'>[]
  notifyOnCreate?: boolean
  notifyChannels?: DeliveryChannel[]
  requiresRsvp?: boolean
  rsvpDeadline?: string
  maxAttendees?: number
  institutionId?: string
}

export interface UpdateEventRequest {
  title?: string
  description?: string
  startDate?: string
  endDate?: string
  isAllDay?: boolean
  timezone?: string
  eventType?: EventType
  color?: string
  location?: EventLocation
  visibility?: 'public' | 'private' | 'targeted'
  targetAudience?: TargetAudience
  reminders?: Omit<EventReminder, 'sent' | 'sentAt'>[]
  notifyChannels?: DeliveryChannel[]
  requiresRsvp?: boolean
  rsvpDeadline?: string
  maxAttendees?: number
  status?: 'draft' | 'published' | 'cancelled'
}

export interface GetEventsParams {
  startDate: string
  endDate: string
  eventType?: EventType
  status?: 'draft' | 'published' | 'cancelled'
  search?: string
  page?: number
  limit?: number
  institutionId?: string
}

// ==================== NOTIFICATION ====================

export interface Notification {
  id: string
  _id: string
  institutionId: string
  userId: string
  sourceType: NotificationType
  sourceId?: string
  title: string
  message: string
  icon?: string
  actionType?: 'link' | 'modal' | 'none'
  actionUrl?: string
  actionData?: Record<string, any>
  status: 'unread' | 'read' | 'archived'
  readAt?: string
  isAcknowledged: boolean
  acknowledgedAt?: string
  priority: AnnouncementPriority
  expiresAt?: string
  channel: DeliveryChannel
  deliveredVia: DeliveryChannel[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface GetNotificationsParams {
  page?: number
  limit?: number
  status?: 'unread' | 'read' | 'archived'
  sourceType?: NotificationType
}

export interface NotificationCounts {
  total: number
  unread: number
  byType: Record<NotificationType, number>
}

export interface NotificationPreferences {
  id: string
  globalEnabled: boolean
  quietHours?: {
    enabled: boolean
    startTime: string
    endTime: string
    timezone: string
  }
  channels: {
    inApp: { enabled: boolean }
    email: { enabled: boolean; frequency: 'instant' | 'daily_digest' | 'weekly_digest' }
    sms: { enabled: boolean }
  }
  categories: {
    announcements: { inApp: boolean; email: boolean; sms: boolean }
    events: { inApp: boolean; email: boolean; sms: boolean }
    attendance: { inApp: boolean; email: boolean; sms: boolean }
    transport: { inApp: boolean; email: boolean; sms: boolean }
    fees: { inApp: boolean; email: boolean; sms: boolean }
    library: { inApp: boolean; email: boolean; sms: boolean }
    meals: { inApp: boolean; email: boolean; sms: boolean }
    system: { inApp: boolean; email: boolean; sms: boolean }
  }
}

// ==================== ANNOUNCEMENT API ====================

export const getAnnouncements = async (
  params: GetAnnouncementsParams = {}
): Promise<{
  announcements: Announcement[]
  pagination: {
    currentPage: number
    totalPages: number
    totalAnnouncements: number
    limit: number
    hasNextPage: boolean
    hasPrevPage: boolean
  }
}> => {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString())
    }
  })
  const response = await apiClient.get(`/communication/announcements?${searchParams}`)
  return response.data.data
}

export const getAnnouncementById = async (id: string): Promise<Announcement> => {
  const response = await apiClient.get(`/communication/announcements/${id}`)
  return response.data.data
}

export const createAnnouncement = async (
  data: CreateAnnouncementRequest
): Promise<Announcement> => {
  const response = await apiClient.post('/communication/announcements', data)
  return response.data.data
}

export const updateAnnouncement = async (
  id: string,
  data: UpdateAnnouncementRequest
): Promise<Announcement> => {
  const response = await apiClient.put(`/communication/announcements/${id}`, data)
  return response.data.data
}

export const deleteAnnouncement = async (id: string): Promise<void> => {
  await apiClient.delete(`/communication/announcements/${id}`)
}

export const sendAnnouncement = async (id: string): Promise<Announcement> => {
  const response = await apiClient.post(`/communication/announcements/${id}/send`)
  return response.data.data
}

export const cancelAnnouncement = async (id: string): Promise<Announcement> => {
  const response = await apiClient.post(`/communication/announcements/${id}/cancel`)
  return response.data.data
}

export const getAnnouncementStats = async (
  institutionId?: string
): Promise<AnnouncementStats> => {
  const params = institutionId ? `?institutionId=${institutionId}` : ''
  const response = await apiClient.get(`/communication/announcements/stats${params}`)
  return response.data.data
}

export const getAnnouncementRecipients = async (
  id: string,
  params: { page?: number; limit?: number; status?: 'delivered' | 'read' | 'acknowledged' } = {}
): Promise<{
  recipients: Array<{
    userId: string
    user: { firstName: string; lastName: string; email: string } | null
    deliveredAt?: string
    readAt?: string
    acknowledgedAt?: string
  }>
  pagination: {
    currentPage: number
    totalPages: number
    total: number
    limit: number
  }
}> => {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) searchParams.append(key, value.toString())
  })
  const response = await apiClient.get(
    `/communication/announcements/${id}/recipients?${searchParams}`
  )
  return response.data.data
}

export const getAudiencePreview = async (
  target: TargetAudience,
  institutionId?: string
): Promise<{
  totalRecipients: number
  breakdown: {
    students: number
    parents: number
    teachers: number
    staff: number
  }
}> => {
  const response = await apiClient.post('/communication/audience/preview', {
    target,
    institutionId
  })
  return response.data.data
}

// ==================== EVENT API ====================

export const getEvents = async (params: GetEventsParams): Promise<CalendarEvent[]> => {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString())
    }
  })
  const response = await apiClient.get(`/communication/events?${searchParams}`)
  return response.data.data
}

export const getUpcomingEvents = async (limit: number = 10): Promise<CalendarEvent[]> => {
  const response = await apiClient.get(`/communication/events/upcoming?limit=${limit}`)
  return response.data.data
}

export const getEventById = async (id: string): Promise<CalendarEvent> => {
  const response = await apiClient.get(`/communication/events/${id}`)
  return response.data.data
}

export const createEvent = async (data: CreateEventRequest): Promise<CalendarEvent> => {
  const response = await apiClient.post('/communication/events', data)
  return response.data.data
}

export const updateEvent = async (
  id: string,
  data: UpdateEventRequest
): Promise<CalendarEvent> => {
  const response = await apiClient.put(`/communication/events/${id}`, data)
  return response.data.data
}

export const deleteEvent = async (id: string): Promise<void> => {
  await apiClient.delete(`/communication/events/${id}`)
}

export const rsvpToEvent = async (
  id: string,
  status: 'attending' | 'maybe' | 'declined',
  notes?: string
): Promise<{ eventId: string; status: string; notes?: string }> => {
  const response = await apiClient.post(`/communication/events/${id}/rsvp`, {
    status,
    notes
  })
  return response.data.data
}

export const getEventRsvps = async (
  id: string,
  status?: 'pending' | 'attending' | 'maybe' | 'declined'
): Promise<{
  rsvps: EventRsvp[]
  summary: {
    attending: number
    maybe: number
    declined: number
    pending: number
  }
}> => {
  const params = status ? `?status=${status}` : ''
  const response = await apiClient.get(`/communication/events/${id}/rsvps${params}`)
  return response.data.data
}

// ==================== NOTIFICATION API ====================

export const getNotifications = async (
  params: GetNotificationsParams = {}
): Promise<{
  notifications: Notification[]
  pagination: {
    currentPage: number
    totalPages: number
    total: number
    limit: number
  }
}> => {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) searchParams.append(key, value.toString())
  })
  const response = await apiClient.get(`/communication/notifications?${searchParams}`)
  return response.data.data
}

export const getNotificationCounts = async (): Promise<NotificationCounts> => {
  const response = await apiClient.get('/communication/notifications/counts')
  return response.data.data
}

export const getUnreadCount = async (): Promise<number> => {
  const response = await apiClient.get('/communication/notifications/unread-count')
  return response.data.data.unread
}

export const markNotificationAsRead = async (id: string): Promise<Notification> => {
  const response = await apiClient.patch(`/communication/notifications/${id}/read`)
  return response.data.data
}

export const markAllNotificationsAsRead = async (): Promise<{ markedAsRead: number }> => {
  const response = await apiClient.patch('/communication/notifications/read-all')
  return response.data.data
}

export const acknowledgeNotification = async (id: string): Promise<Notification> => {
  const response = await apiClient.patch(`/communication/notifications/${id}/acknowledge`)
  return response.data.data
}

export const deleteNotification = async (id: string): Promise<void> => {
  await apiClient.delete(`/communication/notifications/${id}`)
}

export const archiveOldNotifications = async (
  olderThanDays: number = 30
): Promise<{ archived: number }> => {
  const response = await apiClient.post('/communication/notifications/archive', {
    olderThanDays
  })
  return response.data.data
}

// ==================== PREFERENCES API ====================

export const getNotificationPreferences = async (): Promise<NotificationPreferences> => {
  const response = await apiClient.get('/communication/preferences')
  return response.data.data
}

export const updateNotificationPreferences = async (
  updates: Partial<NotificationPreferences>
): Promise<NotificationPreferences> => {
  const response = await apiClient.put('/communication/preferences', updates)
  return response.data.data
}
